import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch'
import IosShareIcon from '@mui/icons-material/IosShare'
import {
  Box,
  Button,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { generatePath, Link as RouterLink, useParams } from 'react-router-dom'

import { AdvisorSelect } from '../../components/Estimates/AdvisorSelect/AdvisorSelect'
import { InspectionStatusSelect } from '../../components/Inspections/InspectionStatusSelect'
import { CustomerLinkWithNotes } from '../../components/SharedUI/CustomerLinkWithNotes'
import { ShareViaContainer } from '../../components/SharedUI/ShareViaContainer'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { useGetCustomersQuery, useGetRepairOrdersQuery } from '../../redux/api'
import {
  useCreateStandaloneApiKeyForCustomerMutation,
  useGetInspectionQuery
} from '../../redux/api/inspectionApi'
import DeleteInspectionMenu from './DeleteInspectionMenu'
import TechnicianSelect from './TechnicianSelect'

const HeadingBox: FC = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [shareLink, setShareLink] = useState<{
    showPrice: boolean
    inviteLink: string
  }>()
  const [menuAnchorRef, setMenuAnchorRef] = useState<HTMLElement | null>(null)
  const { id } = useParams()
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: expanded } = useGetInspectionQuery({
    authorization: accessToken,
    id: id!
  })
  const { data: repairOrders } = useGetRepairOrdersQuery(
    {
      authorization: accessToken,
      shop: shopId,
      repairOrder: [expanded?.inspection?.info?.repairOrder!]
    },
    {
      skip: expanded?.inspection?.info?.repairOrder === undefined
    }
  )
  const repairOrder = repairOrders && repairOrders[0]
  const customerId = repairOrder?.entry?.info?.customer

  const { data: customers } = useGetCustomersQuery(
    {
      authorization: accessToken,
      shop: shopId,
      customer: [customerId!]
    },
    {
      skip: customerId === undefined
    }
  )

  const [getCustomerInspectionLink] =
    useCreateStandaloneApiKeyForCustomerMutation()

  const handleCopyInspectionLink = useCallback(
    async (showPrice: boolean) => {
      const response = await getCustomerInspectionLink({
        authorization: accessToken,
        createStandaloneApiKeyForCustomerRequest: {
          inspectionId: id!,
          showPrice,
          showPartsLabor: false
        }
      })
      if ('error' in response) {
        return
      }
      const inspectionCustomerKey = response.data
      const inviteLink =
        window.location.origin +
        AppRoutes.INSPECTION_RESULTS_RAW +
        inspectionCustomerKey
      setShareLink({ showPrice, inviteLink })
      setMenuOpen(false)
    },
    [accessToken, getCustomerInspectionLink, id]
  )

  const customer = customers && customers[0]

  const isEstimate = repairOrder?.entry?.info?.isEstimate
  const repairOrderNum = repairOrder?.entry?.localId
  const handleToggleShareButton = () => {
    setMenuOpen((prevOpen) => !prevOpen)
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.24)',
        p: 2
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing="5px">
          <ContentPasteSearchIcon fontSize="large" />
          <Typography component="h1" variant="h1">
            {expanded?.inspection?.info?.title || ''}
          </Typography>
        </Stack>
        <Box>
          {shareLink && (
            <ShareViaContainer
              title={
                shareLink.showPrice ? 'Authorization' : 'Inspection Report'
              }
              link={shareLink.inviteLink}
              handleClose={() => {
                setShareLink(undefined)
              }}
              customer={customer}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<IosShareIcon />}
            onClick={(event: MouseEvent<HTMLElement>) => {
              setMenuAnchorRef(event.currentTarget)
              handleToggleShareButton()
            }}>
            Share
          </Button>
          <Menu
            anchorEl={menuAnchorRef}
            open={menuOpen}
            onClose={() => {
              setMenuAnchorRef(null)
              setMenuOpen(false)
            }}>
            <MenuItem
              data-testid={'copyReportLinkButton'}
              onClick={handleCopyInspectionLink.bind(null, false)}>
              Generate Inspection Report
            </MenuItem>
            <MenuItem
              data-testid={'copyAuthorizationLinkButton'}
              onClick={handleCopyInspectionLink.bind(null, true)}>
              Generate Authorization
            </MenuItem>
          </Menu>

          {expanded?.inspection && (
            <DeleteInspectionMenu inspection={expanded.inspection} />
          )}
        </Box>
      </Stack>
      <Stack direction="row" spacing={1}>
        {repairOrder && (
          <>
            <Typography sx={{ fontWeight: 'bold' }}>Repair Order: </Typography>
            <Link
              component={RouterLink}
              to={generatePath(AppRoutes.ESTIMATE_DETAILS, {
                id: repairOrder.entry!.id!
              })}>
              {isEstimate ? 'Est' : 'RO #'}
              {repairOrderNum}
            </Link>
          </>
        )}
        {customer && (
          <>
            <Typography sx={{ fontWeight: 'bold' }}>Customer:</Typography>
            <CustomerLinkWithNotes customer={customer} />
            <Typography>
              {` - ${
                customer.information!.phone
                  ? customer.information!.phone
                  : customer.information!.email
              }`}
            </Typography>
          </>
        )}
        {repairOrder && (
          <>
            <Typography sx={{ fontWeight: 'bold' }}>Advisor:</Typography>
            <AdvisorSelect repairOrder={repairOrder.entry!} />
          </>
        )}
        <Typography sx={{ fontWeight: 'bold' }}>Technician:</Typography>
        <TechnicianSelect inspection={expanded?.inspection} />
        <Typography sx={{ fontWeight: 'bold' }}>Status:</Typography>
        {expanded && (
          <InspectionStatusSelect inspection={expanded?.inspection!} />
        )}
      </Stack>
    </Paper>
  )
}

export default HeadingBox
